import { UserRole } from 'dy-frontend-http-repository/lib/data/enums';
import {
    TaskParticipationResource,
    TeamParticipationResource,
} from 'dy-frontend-http-repository/lib/modules/Task/resources';
import { UpdateTimeTrackingEntryInput } from 'dy-frontend-http-repository/lib/modules/TimeTacking/inputs';
import { TimeTrackingEntryResource } from 'dy-frontend-http-repository/lib/modules/TimeTacking/resources';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import Avatar from '@app/components/Avatar';
import Circle from '@app/components/Circle';
import Grid from '@app/components/Grid';
import SelectFormField from '@app/components/SelectFormField';
import DevText from '@app/components/Text';
import TextAreaFormField from '@app/components/TextAreaFormField';
import { $task } from '@app/containers/pages/Task/store/states';
import { $authorizedUser } from '@app/containers/store/states';
import { imageHashPreview } from '@app/data/consts';
import { useCustomSelectFormField, useForm, useTextFormField } from '@app/hooks';
import { getStringMaxLengthValidator, getStringRequiredValidator } from '@app/hooks/validation/functions';
import { Button, Card, Classes, Colors, Divider, FormGroup, Intent, Popover } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import { ModalProps } from '@modals/types';

import { workReportHoursList, workReportMinutesList, workReportTypesInformation } from '../../consts';
import { timeTrackingEntriesApi } from '../../store/apis';
import { updateWorkReport } from '../../store/effects';

import { SelectedActorUser } from './types';

export interface UpdateWorkReportModalProps {
    workReportId: ID;
    taskId: ID;
    durationMin: number;
    comment: string;
    type: string;
}

type Props = ModalProps<UpdateWorkReportModalProps>;

const commentValidators = [getStringMaxLengthValidator({ maxStringLength: 250 })];
const workReportTypeValidators = [getStringRequiredValidator()];
const hoursValidators = [getStringRequiredValidator()];
const minutesValidators = [getStringRequiredValidator()];

const allowedWorkReportActorSwitchRoles = [
    UserRole.CORE_ROOT,
    UserRole.CORE_ADMIN,
    UserRole.CORE_PLATFORM_DEV,
    UserRole.CORE_OPERATIONS_MANAGER,
    UserRole.CORE_ACCOUNT_DIRECTOR,
    UserRole.CORE_CREATIVE_DIRECTOR,
    UserRole.CORE_HR,
];

const UpdateWorkReportModal: React.FC<Props> = ({ closeModal, data }) => {
    const task = useStore($task);
    const me = useStore($authorizedUser);
    const workReportType = data?.type;

    const comment = useTextFormField({
        id: 'comment',
        validators: commentValidators,
        initialValue: data?.comment ?? '',
    });

    const hoursDecimal = data?.durationMin ? data.durationMin / 60 : 0;
    const initialHours = Math.trunc(hoursDecimal);
    const initialMinutes = Math.round((hoursDecimal % 1) * 60);

    const hours = useCustomSelectFormField<string | null>({
        id: 'hours',
        validators: hoursValidators,
        initialValue: initialHours === 0 ? '' : `${initialHours}`,
        formatValue: (value) => value ?? '',
    });

    const minutes = useCustomSelectFormField<string | null>({
        id: 'minutes',
        validators: minutesValidators,
        initialValue: `${initialMinutes}`,
        formatValue: (value) => value ?? '',
    });

    const form = useForm({
        fields: [comment, hours, minutes],

        apiCall: async () => {
            try {
                if (!workReportType) {
                    throw new Error('The work report category is required for an update.');
                }

                // Create input
                const updateTimeTrackingEntry: UpdateTimeTrackingEntryInput = {
                    comment: comment.value,
                    date_at: moment().utc().format('YYYY-MM-DD'),
                    duration_min: parseInt(hours.value ?? '0') * 60 + parseInt(minutes.value ?? '0'),
                    type: workReportType,
                };

                // Get work report ID
                const workReportId = data?.workReportId ?? '0';

                // Update time tracking entry
                await updateWorkReport({ id: workReportId, input: updateTimeTrackingEntry });

                // Update time tracking entry in local state
                timeTrackingEntriesApi.update({ id: workReportId, input: updateTimeTrackingEntry });

                // Close modal
                closeModal?.();

                return { response: true };
            } catch (e) {
                throw e;
            }
        },
        onFailure: (error) => {
            // TODO: handle error
            console.error(error);
        },
    });

    const [selectedActor, setSelectedActor] = useState<SelectedActorUser | null>(null);

    useEffect(() => {
        if (me === null) {
            return;
        }

        if (selectedActor === null) {
            // Selected actor is NOT initialized yet
            setSelectedActor({
                id: me.user.id,
                firstName: me.user.first_name,
                lastName: me.user.last_name,
                imageHash: me.user.image_hash,
            });
        }
    }, [me]);

    if (!data || !me) {
        return null;
    }

    const renderSelectParticipantPopoverContent = (participants: TaskParticipationResource[]) => {
        const renderTeamParticipation = (teamParticipation: TeamParticipationResource[]) => {
            if (teamParticipation.length === 0) {
                return (
                    <Flex align="center">
                        <Circle className="mr-1" size="12px" color="none" borderColor={Colors.BLACK} />
                        <DevText muted>No team</DevText>
                    </Flex>
                );
            }

            return teamParticipation.map((tp, index) => (
                <Flex key={tp.assigned_at} align="center" className={index !== 0 ? 'mt-small' : ''}>
                    <Circle className="mr-1" size="12px" borderColor="none" color={tp.team.color} />
                    <DevText muted>{tp.team.title}</DevText>
                </Flex>
            ));
        };

        // Get avatar src
        let avatarSrc: string | null = null;
        if (me.user.image_hash) {
            avatarSrc = imageHashPreview.userImage(me.user.image_hash, ImageHashPreviewSize.XS);
        }

        return (
            <Card compact>
                <Flex direction="row" align="center" justify="space-between">
                    <Flex className="mr-2" direction="row" align="center">
                        <Avatar
                            className="mr-2"
                            width="35px"
                            height="35px"
                            alt={`${me.user.first_name} ${me.user.last_name}`}
                            src={avatarSrc}
                        />
                        <DevText>
                            {me.user.first_name} {me.user.last_name}
                        </DevText>
                    </Flex>

                    <Button
                        minimal
                        disabled={selectedActor !== null && selectedActor.id === me.user.id}
                        icon="plus"
                        className={Classes.POPOVER_DISMISS}
                        intent={Intent.SUCCESS}
                        onClick={() =>
                            setSelectedActor({
                                id: me.user.id,
                                firstName: me.user.first_name,
                                lastName: me.user.last_name,
                                imageHash: me.user.image_hash,
                            })
                        }
                    />
                </Flex>

                {participants.map((participation) => {
                    const { id, first_name, last_name, image_hash, team_participation } = participation.user;

                    // Get avatar src
                    let avatarSrc: string | null = null;
                    if (image_hash) {
                        avatarSrc = imageHashPreview.userImage(image_hash, ImageHashPreviewSize.XS);
                    }

                    return (
                        <Flex key={participation.user.id} className="mt-2" justify="space-between">
                            <Flex className="mr-2" direction="row">
                                <Avatar
                                    className="mr-2"
                                    width="35px"
                                    height="35px"
                                    alt={`${first_name} ${last_name}`}
                                    src={avatarSrc}
                                />

                                <Flex direction="column" align="flex-start" justify="center">
                                    <DevText>
                                        {first_name} {last_name}
                                    </DevText>

                                    {renderTeamParticipation(team_participation)}
                                </Flex>
                            </Flex>

                            <Button
                                minimal
                                disabled={selectedActor !== null && selectedActor.id === id}
                                icon="plus"
                                className={Classes.POPOVER_DISMISS}
                                intent={Intent.SUCCESS}
                                onClick={() =>
                                    setSelectedActor({
                                        id,
                                        firstName: first_name,
                                        lastName: last_name,
                                        imageHash: image_hash,
                                    })
                                }
                            />
                        </Flex>
                    );
                })}
            </Card>
        );
    };

    const renderSelectedActorCard = () => {
        if (!selectedActor) {
            return <div className={Classes.SKELETON} style={{ background: Colors.DARK_GRAY2, height: '75px' }}></div>;
        }

        // Get avatar srcD
        let avatarSrc: string | null = null;
        if (selectedActor.imageHash) {
            avatarSrc = imageHashPreview.userImage(selectedActor.imageHash, ImageHashPreviewSize.XS);
        }

        // Get actor name
        const fullName = `${selectedActor.firstName} ${selectedActor.lastName}`;

        // Get participants
        const participants = task?.participants ?? [];

        return (
            <Card compact style={{ background: Colors.DARK_GRAY2 }}>
                <Flex direction="row" justify="space-between">
                    <Flex direction="row" align="center">
                        <Avatar className="mr-1" width="35px" height="35px" src={avatarSrc} alt={fullName} />
                        <DevText>
                            {fullName}

                            {me.user.id === selectedActor.id && (
                                <DevText inline muted className="ml-1">
                                    (You)
                                </DevText>
                            )}
                        </DevText>
                    </Flex>

                    {participants.length > 0 && (
                        <Popover content={renderSelectParticipantPopoverContent(participants)}>
                            <Button className={Classes.POPOVER_DISMISS} icon="swap-vertical">
                                Switch
                            </Button>
                        </Popover>
                    )}
                </Flex>
            </Card>
        );
    };

    const renderWorkReportForUserField = () => {
        const isUserSwitchAllowed = allowedWorkReportActorSwitchRoles.includes(me?.user.role);
        if (!isUserSwitchAllowed) {
            return null;
        }

        return (
            <div className="mb-2">
                <FormGroup label="Report for">{renderSelectedActorCard()}</FormGroup>
            </div>
        );
    };

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Heading type="h4">Update work report</Heading>
                    <Button minimal icon="cross" onClick={closeModal} />
                </Flex>

                <Divider className="mb-2" />

                <form onSubmit={form.handleFormSubmit}>
                    {/* Report for */}
                    {renderWorkReportForUserField()}

                    {/* Duration */}
                    <Grid container>
                        <Grid xs={6} lg={6}>
                            <SelectFormField
                                field={hours}
                                items={workReportHoursList}
                                formGroupProps={{ label: 'Hours worked' }}
                                selectProps={{
                                    selectButtonProps: { placeholder: 'Select hours being worked', icon: null },
                                }}
                            />
                        </Grid>

                        <Grid xs={6} lg={6}>
                            <SelectFormField
                                field={minutes}
                                items={workReportMinutesList}
                                formGroupProps={{ label: 'Minutes worked' }}
                                selectProps={{
                                    selectButtonProps: { placeholder: 'Select minutes being worked', icon: null },
                                }}
                                itemToReadableFormatter={(item) => item.padStart(2, '0')}
                            />
                        </Grid>
                    </Grid>

                    {/* Comment */}
                    <TextAreaFormField
                        field={comment}
                        formGroupProps={{ label: 'Comment' }}
                        textAreaProps={{ placeholder: 'Enter comment' }}
                    />

                    <Flex className="mt-2" justify="flex-end">
                        <Button className="mr-1" outlined onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button
                            disabled={form.hasFieldErrors || selectedActor === null}
                            loading={form.isSubmitting}
                            type="submit"
                            intent={Intent.PRIMARY}
                        >
                            Update
                        </Button>
                    </Flex>
                </form>
            </Card>
        </Overlay>
    );
};

export default UpdateWorkReportModal;
